import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Alert } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import {
  FiUsers, FiHelpCircle, FiGlobe,
  FiRefreshCw, FiBarChart, FiThumbsUp, FiThumbsDown,
  FiActivity
} from 'react-icons/fi';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  TimeScale,
  Filler,
  ArcElement
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { DatesProvider, DatePickerInput } from '@mantine/dates';
import { MantineProvider, rem } from '@mantine/core';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { Tooltip } from 'react-tooltip';
import { useGlobal } from '../../Context/GlobalContext';
import { useWorkspace } from '../../Context/WorkspaceContext';
import { useLanguage } from '../../Context/LanguageContext';
import './agent-analytics.css';
import ExportDropdown from '../../Components/ExportDropdown';
import { AgentAnalyticsSkeleton } from '../../Components/SkeletonLoaders';
import OutlinedDangerButton from '../../Components/Buttons/OutlinedDangerButton';
import OutlinedPrimaryButton from '../../Components/Buttons/OutlinedPrimaryButton';

// Initialize dayjs UTC plugin
dayjs.extend(utc);

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  TimeScale,
  Filler,
  ArcElement
);

// Modern color palette for charts
const USER_COLOR = '#1e95e0';        // Professional blue
const QUESTION_COLOR = '#ff8e03';     // Vibrant orange
const LIKE_COLOR = '#28a745';         // Success green
const DISLIKE_COLOR = '#dc3545';      // Danger red
const SOURCE_COLOR = '#5eadf7';       // Light blue
const MODEL_COLOR = '#6f42c1';        // Purple

// URL for the world map TopoJSON data
const geoUrl =
  "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Chart.js options for consistent styling
const commonChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top',
      labels: {
        usePointStyle: true,
        padding: 20,
        font: {
          size: 13,
          family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
          weight: '500'
        },
        color: '#374151'
      }
    },
    tooltip: {
      backgroundColor: 'rgba(255, 255, 255, 0.98)',
      titleColor: '#1f2937',
      bodyColor: '#4b5563',
      borderColor: '#d1d5db',
      borderWidth: 1,
      cornerRadius: 12,
      displayColors: true,
      padding: 16,
      titleFont: {
        size: 14,
        weight: '600'
      },
      bodyFont: {
        size: 13,
        weight: '500'
      },
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
    }
  },
  scales: {
    x: {
      grid: {
        color: 'rgba(156, 163, 175, 0.2)',
        drawBorder: false,
        lineWidth: 1
      },
      ticks: {
        font: {
          size: 11,
          family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
        },
        color: '#6b7280',
        padding: 8
      }
    },
    y: {
      beginAtZero: true,
      grid: {
        color: 'rgba(156, 163, 175, 0.2)',
        drawBorder: false,
        lineWidth: 1
      },
      ticks: {
        font: {
          size: 11,
          family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
        },
        color: '#6b7280',
        padding: 8
      }
    }
  },
  interaction: {
    intersect: false,
    mode: 'index'
  },
  elements: {
    line: {
      tension: 0.4,
      borderWidth: 3
    },
    point: {
      radius: 4, // Show points by default (was 0)
      hoverRadius: 8,
      hitRadius: 15,
      borderWidth: 2
    },
    bar: {
      borderRadius: 8,
      borderSkipped: false
    }
  },
  animation: {
    duration: 800,
    easing: 'easeInOutQuart'
  }
};

// Main component
const AgentAnalytics = () => {
  const { id: agentId } = useParams();
  const { apiUrl } = useGlobal();
  const { workspaceData } = useWorkspace();
  const { t } = useLanguage();

  // Component state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [configError, setConfigError] = useState(null);
  const [batchAnalyticsData, setBatchAnalyticsData] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [tooltipContent, setTooltipContent] = useState("");
  const [mapScale, setMapScale] = useState(160);
  const [mapCenter, setMapCenter] = useState([0, 30]);

  // Add state for agent name
  const [agentName, setAgentName] = useState("");

  // Format number with commas
  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Format date for display in charts (YYYY-MM-DD)
  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return '';
    return dayjs(dateStr).format('YYYY-MM-DD');
  };


  // Calculate min date based on workspace chat history limit
  const getMinDate = () => {
    if (!workspaceData || !workspaceData.workspace_bv_chat_history_day_limit) return null;

    const minDate = new Date();
    minDate.setDate(minDate.getDate() - workspaceData.workspace_bv_chat_history_day_limit);
    return minDate;
  };

  // Transform daily data for Chart.js line charts
  const transformDailyDataForChart = (dailyCounts, label, color) => {
    if (!dailyCounts) return { labels: [], datasets: [] };

    const sortedEntries = Object.entries(dailyCounts)
      .sort(([a], [b]) => dayjs(a).unix() - dayjs(b).unix());

    let labels = sortedEntries.map(([date]) => formatDateForDisplay(date));
    const data = sortedEntries.map(([, count]) => count);

    // Show points especially when there's limited data
    const pointRadius = data.length <= 3 ? 6 : 4;

    // Center single data point by adding empty labels on both sides
    if (labels.length === 1) {
      labels = ['', labels[0], ''];
      data.unshift(null);
      data.push(null);
    }

    return {
      labels,
      datasets: [{
        label,
        data,
        borderColor: color,
        backgroundColor: color + '30',
        borderWidth: 3,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: color,
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: pointRadius, // Dynamic point size based on data count
        pointHoverRadius: 8,
        pointHoverBackgroundColor: color,
        pointHoverBorderColor: '#ffffff',
        pointHoverBorderWidth: 3,
        spanGaps: false // Don't connect null values
      }]
    };
  };

  // Transform like/dislike data for Chart.js
  const transformLikeDislikeDataForChart = (dailyLikesDislikes) => {
    if (!dailyLikesDislikes) return { labels: [], datasets: [] };

    const sortedEntries = Object.entries(dailyLikesDislikes)
      .sort(([a], [b]) => dayjs(a).unix() - dayjs(b).unix());

    let labels = sortedEntries.map(([date]) => formatDateForDisplay(date));
    let likedData = sortedEntries.map(([, counts]) => counts.liked || 0);
    let dislikedData = sortedEntries.map(([, counts]) => counts.disliked || 0);

    // Show points especially when there's limited data
    const pointRadius = labels.length <= 3 ? 6 : 4;

    // Center single data point by adding empty labels on both sides
    if (labels.length === 1) {
      labels = ['', labels[0], ''];
      likedData.unshift(null);
      likedData.push(null);
      dislikedData.unshift(null);
      dislikedData.push(null);
    }

    return {
      labels,
      datasets: [
        {
          label: t('dashboard.agentanalytics.likedLabel'),
          data: likedData,
          borderColor: LIKE_COLOR,
          backgroundColor: LIKE_COLOR + '30',
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointBackgroundColor: LIKE_COLOR,
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: pointRadius, // Dynamic point size based on data count
          pointHoverRadius: 8,
          pointHoverBackgroundColor: LIKE_COLOR,
          pointHoverBorderColor: '#ffffff',
          pointHoverBorderWidth: 3,
          spanGaps: false // Don't connect null values
        },
        {
          label: t('dashboard.agentanalytics.dislikedLabel'),
          data: dislikedData,
          borderColor: DISLIKE_COLOR,
          backgroundColor: DISLIKE_COLOR + '30',
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointBackgroundColor: DISLIKE_COLOR,
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: pointRadius, // Dynamic point size based on data count
          pointHoverRadius: 8,
          pointHoverBackgroundColor: DISLIKE_COLOR,
          pointHoverBorderColor: '#ffffff',
          pointHoverBorderWidth: 3,
          spanGaps: false // Don't connect null values
        }
      ]
    };
  };

  // Transform categorical data for Chart.js bar charts
  const transformCategoricalDataForChart = (counts, label, color) => {
    if (!counts) return { labels: [], datasets: [] };

    const sortedEntries = Object.entries(counts)
      .sort(([, a], [, b]) => b - a);

    const labels = sortedEntries.map(([name]) => name);
    const data = sortedEntries.map(([, count]) => count);

    // Create gradient colors for each bar
    const backgroundColors = data.map(() => color + '60');
    const borderColors = data.map(() => color);

    return {
      labels,
      datasets: [{
        label,
        data,
        backgroundColor: backgroundColors,
        borderColor: borderColors,
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
        hoverBackgroundColor: color + '80',
        hoverBorderColor: color,
        hoverBorderWidth: 3
      }]
    };
  };

  // Mapping from backend country names (lowercase) to map display names (lowercase)
  const countryNameMapping = useMemo(() => ({
    'türkiye': 'turkey',
    'united states': 'united states of america', // Map often uses the full name
    'the netherlands': 'netherlands',
    'czechia': 'czech republic', // Map often uses the full name
    'congo republic': 'republic of the congo', // Check map data if issues persist
    'dr congo': 'democratic republic of the congo', // Check map data if issues persist
    "ivory coast": "côte d'ivoire", // Map often uses the French name
    'st kitts and nevis': 'saint kitts and nevis',
    'st vincent and grenadines': 'saint vincent and the grenadines',
    'são tomé and príncipe': 'sao tome and principe',
    // Add more mappings here if needed based on testing
  }), []); // Empty dependency array, mapping is static

  // Memoize the country counts lookup for performance, normalizing keys
  const countryCountsMap = useMemo(() => {
    if (!batchAnalyticsData?.country_question_counts) return new Map();

    const normalizedMap = new Map();
    for (const [backendName, count] of Object.entries(batchAnalyticsData.country_question_counts)) {
      const backendNameLower = backendName.toLowerCase();
      // Use the mapped name if available, otherwise use the original (lowercased) backend name
      const mapKey = countryNameMapping[backendNameLower] || backendNameLower;
      // Aggregate counts if multiple backend names map to the same map name
      normalizedMap.set(mapKey, (normalizedMap.get(mapKey) || 0) + count);
    }
    return normalizedMap;
    // Use the mapping object as a dependency as well
  }, [batchAnalyticsData, countryNameMapping]);

  // Prepare sorted country data for the list/table
  const sortedCountryData = useMemo(() => {
    if (!countryCountsMap) return [];
    return Array.from(countryCountsMap.entries())
      .map(([name, count]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), count })) // Capitalize name for display
      .filter(item => item.count > 0) // Filter out countries with 0 counts
      .sort((a, b) => b.count - a.count); // Sort by count descending
  }, [countryCountsMap]);

  // Find max country count for color scaling (optional)
  const maxCountryCount = useMemo(() => {
    if (!batchAnalyticsData?.country_question_counts) return 0;
    return Math.max(...Object.values(batchAnalyticsData.country_question_counts));
  }, [batchAnalyticsData]);

  // Fetch agent name (similar to AgentChats)
  const fetchAgentName = useCallback(async () => {
    if (!agentId || !apiUrl) return; // Guard clause

    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');
      if (!brandvoxToken) {
        return;
      }

      // Check cache first
      const cachedAgentNames = JSON.parse(localStorage.getItem('cached_agent_names') || '{}');
      if (cachedAgentNames[agentId]) {
        setAgentName(cachedAgentNames[agentId]);
        return;
      }

      const response = await fetch(
        `${apiUrl}/api/v1/chatbot/get-chatbot`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${brandvoxToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ chatbot_id: agentId })
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data && data.chatbot_name) {
          setAgentName(data.chatbot_name);
          // Update cache
          cachedAgentNames[agentId] = data.chatbot_name;
          localStorage.setItem('cached_agent_names', JSON.stringify(cachedAgentNames));
        }
      } else {
      }
    } catch (err) {
      // Don't set component error state for this optional fetch
    }
  }, [apiUrl, agentId]); // Removed t dependency as it's not used here

  // Fetch batch analytics data
  const fetchBatchAnalytics = useCallback(async (currentDateRange) => {
    if (!agentId || !apiUrl) {
      setConfigError(t('dashboard.agentanalytics.errorMissingApiUrl'));
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');
      if (!brandvoxToken) {
        throw new Error('Authentication is required to view analytics.');
      }

      const [startDate, endDate] = currentDateRange; // Use passed argument
      let formattedStartDate = null;
      let formattedEndDate = null;
      const url = `${apiUrl}/api/v1/chatbot/get-chatbot-analytics-batch`;

      // Build request body for the new v1 API
      const requestBody = {
        chatbot_id: agentId
      };

      // Only format and add date parameters if both dates are valid
      if (startDate instanceof Date && endDate instanceof Date) {
        formattedStartDate = dayjs(startDate).utc().startOf('day').toISOString();
        formattedEndDate = dayjs(endDate).utc().endOf('day').toISOString();
        requestBody.start_date = formattedStartDate;
        requestBody.end_date = formattedEndDate;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${brandvoxToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch analytics data' }));
        throw new Error(errorData.detail || `Failed to fetch analytics data (Status: ${response.status})`);
      }

      const data = await response.json();

      // Transform the new API response to match expected format
      const transformedData = {
        total_user_count: 0,
        total_question_count: 0,
        total_answer_count: 0,
        total_liked_count: 0,
        total_disliked_count: 0,
        daily_user_counts: {},
        daily_question_counts: {},
        daily_like_dislike_counts: {},
        country_question_counts: {},
        source_question_counts: {},
        model_question_counts: {},
      };

      // Process daily analytics
      data.analytics.forEach(dayData => {
        const date = dayData.date;
        transformedData.daily_user_counts[date] = dayData.unique_sessions;
        transformedData.daily_question_counts[date] = dayData.total_questions;

        // Process like/dislike data
        transformedData.daily_like_dislike_counts[date] = {
          liked: dayData.liked_stats.liked || 0,
          disliked: dayData.liked_stats.disliked || 0
        };

        // Aggregate totals across all days
        transformedData.total_user_count += dayData.unique_sessions || 0;
        transformedData.total_question_count += dayData.total_questions || 0;
        transformedData.total_answer_count += dayData.total_answers || 0;
        transformedData.total_liked_count += dayData.liked_stats.liked || 0;
        transformedData.total_disliked_count += dayData.liked_stats.disliked || 0;

        // Aggregate countries
        Object.entries(dayData.countries).forEach(([country, count]) => {
          transformedData.country_question_counts[country] =
            (transformedData.country_question_counts[country] || 0) + count;
        });

        // Aggregate sources (questions only)
        Object.entries(dayData.sources_questions || {}).forEach(([source, count]) => {
          transformedData.source_question_counts[source] =
            (transformedData.source_question_counts[source] || 0) + count;
        });

        // Aggregate models (answers only)
        Object.entries(dayData.models_answers || {}).forEach(([model, count]) => {
          transformedData.model_question_counts[model] =
            (transformedData.model_question_counts[model] || 0) + count;
        });
      });

      setBatchAnalyticsData(transformedData);

      // Update date range picker ONLY if the fetch was done without specific dates passed in.
      if (data.analytics?.date_range && !(startDate instanceof Date && endDate instanceof Date)) {
        try {
          const backendStartDate = dayjs.utc(data.analytics.date_range.start).toDate();
          const backendEndDate = dayjs.utc(data.analytics.date_range.end).toDate();
          if (backendStartDate instanceof Date && !isNaN(backendStartDate) &&
            backendEndDate instanceof Date && !isNaN(backendEndDate)) {
            // No need to compare with state here, just set it if the fetch was parameterless
            setDateRange([backendStartDate, backendEndDate]);
          } else {
          }
        } catch (parseError) {
        }
      }

    } catch (err) {
      setError(err.message || 'An unknown error occurred');
      setBatchAnalyticsData(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, agentId, t]); // Keep only necessary stable dependencies

  // Handle date changes from the picker
  const handleDateChange = (newDateRange) => {
    setDateRange(newDateRange);
    const [startDate, endDate] = newDateRange;
    // Fetch only if the range is complete (both dates selected) or cleared (both null)
    if ((startDate instanceof Date && endDate instanceof Date) || (!startDate && !endDate)) {
      // Use a slight delay to ensure state update has propagated if needed, though passing directly is better
      // setTimeout(() => fetchBatchAnalytics(newDateRange), 0);
      fetchBatchAnalytics(newDateRange); // Pass the new range directly
    }
  };

  // Add config validation on mount
  useEffect(() => {
    setConfigError(null);
    if (!agentId) {
      setConfigError(t('dashboard.agentanalytics.errorMissingAgentId'));
      setLoading(false);
      return;
    }
    if (!apiUrl) {
      setConfigError(t('dashboard.agentanalytics.errorMissingApiUrl'));
      setLoading(false);
      return;
    }
    if (!localStorage.getItem('brandvox_token')) {
      setConfigError(t('dashboard.agentanalytics.errorMissingAuthToken'));
      setLoading(false);
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, apiUrl]);

  // Initial data fetch including agent name
  useEffect(() => {
    if (!configError && agentId && apiUrl) {
      fetchAgentName(); // Fetch agent name
      fetchBatchAnalytics(dateRange); // Fetch analytics
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, apiUrl, configError, dateRange]);

  // Effect to handle responsive map scaling and centering
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      // Adjust scale and center based on screen width breakpoints
      if (width < 768) { // Mobile breakpoint (adjust as needed)
        setMapScale(120); // Example mobile scale
        setMapCenter([0, 40]); // Mobile center (further north)
      } else { // Tablet/Desktop
        setMapScale(160); // Example desktop scale (adjust if needed based on previous edits)
        setMapCenter([0, 30]); // Desktop center
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Call once on mount

    return () => window.removeEventListener('resize', handleResize);
  }, []); // Empty dependency array


  // Create header component for reuse (both desktop and mobile versions)
  const headerComponent = (
    <>
      {/* Professional header - only visible on desktop */}
      <div className="analytics-page-header d-none d-md-block">
        <div className="header-content">
          <div className="header-icon-analytics">
            <FiBarChart />
          </div>
          <div className="header-text">
            <h4>{t('dashboard.agentanalytics.pageTitle')}</h4>
            <p>{t('dashboard.agentanalytics.pageSubtitle')}</p>
          </div>
          <div className="header-actions">
            <div className="date-picker-wrapper">
              <DatePickerInput
                type="range"
                value={dateRange}
                onChange={handleDateChange}
                placeholder={t('dashboard.agentanalytics.selectDateRange')}
                mx="auto"
                maw={400}
                size="sm"
                radius="md"
                clearable
                minDate={getMinDate()}
                styles={{
                  input: {
                    fontSize: rem(14),
                    paddingTop: rem(8),
                    paddingBottom: rem(8),
                  },
                  day: {
                    borderRadius: '50%',
                  },
                  levelsGroup: {
                    borderRadius: rem(10),
                    boxShadow: '0 5px 20px rgba(0, 0, 0, 0.05)',
                    border: 'none',
                  }
                }}
              />
            </div>
            <ExportDropdown
              data={batchAnalyticsData ? [
                {
                  metric: t('dashboard.agentanalytics.exportMetricTotalUsers'),
                  value: formatNumber(batchAnalyticsData.total_user_count),
                  category: t('dashboard.agentanalytics.exportCategorySummary')
                },
                {
                  metric: t('dashboard.agentanalytics.exportMetricTotalQuestions'),
                  value: formatNumber(batchAnalyticsData.total_question_count),
                  category: t('dashboard.agentanalytics.exportCategorySummary')
                },
                {
                  metric: t('dashboard.agentanalytics.exportMetricTotalAnswers'),
                  value: formatNumber(batchAnalyticsData.total_answer_count),
                  category: t('dashboard.agentanalytics.exportCategorySummary')
                },
                {
                  metric: t('dashboard.agentanalytics.exportMetricTotalLiked'),
                  value: formatNumber(batchAnalyticsData.total_liked_count),
                  category: t('dashboard.agentanalytics.exportCategorySummary')
                },
                {
                  metric: t('dashboard.agentanalytics.exportMetricTotalDisliked'),
                  value: formatNumber(batchAnalyticsData.total_disliked_count),
                  category: t('dashboard.agentanalytics.exportCategorySummary')
                },
                ...Object.entries(batchAnalyticsData.daily_user_counts || {}).map(([date, count]) => ({
                  metric: t('dashboard.agentanalytics.exportMetricDailyUsers'),
                  value: formatNumber(count),
                  date: formatDateForDisplay(date),
                  category: t('dashboard.agentanalytics.exportCategoryDaily')
                })),
                ...Object.entries(batchAnalyticsData.daily_question_counts || {}).map(([date, count]) => ({
                  metric: t('dashboard.agentanalytics.exportMetricDailyQuestions'),
                  value: formatNumber(count),
                  date: formatDateForDisplay(date),
                  category: t('dashboard.agentanalytics.exportCategoryDaily')
                })),
                ...Object.entries(batchAnalyticsData.country_question_counts || {}).map(([country, count]) => ({
                  metric: t('dashboard.agentanalytics.exportMetricQuestionsByCountry'),
                  value: formatNumber(count),
                  country: country,
                  category: t('dashboard.agentanalytics.exportCategoryGeographic')
                })),
                ...Object.entries(batchAnalyticsData.source_question_counts || {}).map(([source, count]) => ({
                  metric: t('dashboard.agentanalytics.exportMetricQuestionsBySource'),
                  value: formatNumber(count),
                  source: source,
                  category: t('dashboard.agentanalytics.exportCategorySources')
                })),
                ...Object.entries(batchAnalyticsData.model_question_counts || {}).map(([model, count]) => ({
                  metric: t('dashboard.agentanalytics.exportMetricAnswersByModel'),
                  value: formatNumber(count),
                  model: model,
                  category: t('dashboard.agentanalytics.exportCategoryModels')
                }))
              ] : []}
              filename={`analytics-${agentName || 'agent'}`}
              appliedFilters={{
                agent_name: agentName,
                start_date: dateRange[0] ? dayjs(dateRange[0]).format('YYYY-MM-DD') : null,
                end_date: dateRange[1] ? dayjs(dateRange[1]).format('YYYY-MM-DD') : null
              }}
              agentName={agentName}
              isLoading={loading}
              className="header-action-btn"
            />
          </div>
        </div>
      </div>

      {/* Mobile header */}
      <div className="mobile-analytics-header d-md-none">
        <div className="mobile-header-content">
          <div className="mobile-title">
            <FiBarChart className="me-2" />
            {t('dashboard.agentanalytics.pageTitle')}
          </div>
          <div className="mobile-actions">
            <div className="date-picker-wrapper-mobile">
              <DatePickerInput
                type="range"
                value={dateRange}
                onChange={handleDateChange}
                placeholder={t('dashboard.agentanalytics.selectDateRange')}
                size="xs"
                radius="md"
                clearable
                minDate={getMinDate()}
              />
            </div>
            <ExportDropdown
              data={batchAnalyticsData ? [
                {
                  metric: t('dashboard.agentanalytics.exportMetricTotalUsers'),
                  value: formatNumber(batchAnalyticsData.total_user_count),
                  category: t('dashboard.agentanalytics.exportCategorySummary')
                },
                {
                  metric: t('dashboard.agentanalytics.exportMetricTotalQuestions'),
                  value: formatNumber(batchAnalyticsData.total_question_count),
                  category: t('dashboard.agentanalytics.exportCategorySummary')
                },
                {
                  metric: t('dashboard.agentanalytics.exportMetricTotalAnswers'),
                  value: formatNumber(batchAnalyticsData.total_answer_count),
                  category: t('dashboard.agentanalytics.exportCategorySummary')
                }
              ] : []}
              filename={`analytics-${agentName || 'agent'}`}
              appliedFilters={{
                agent_name: agentName,
                start_date: dateRange[0] ? dayjs(dateRange[0]).format('YYYY-MM-DD') : null,
                end_date: dateRange[1] ? dayjs(dateRange[1]).format('YYYY-MM-DD') : null
              }}
              agentName={agentName}
              isLoading={loading}
              className="mobile-export-btn"
            />
          </div>
        </div>
      </div>
    </>
  );

  // Render loading state
  if (loading) {
    return (
      <MantineProvider>
        <DatesProvider settings={{ locale: 'en', firstDayOfWeek: 0, weekendDays: [0] }}>
          <AgentAnalyticsSkeleton pageHeader={headerComponent} />
        </DatesProvider>
      </MantineProvider>
    );
  }

  // Render configuration error state
  if (configError) {
    return (
      <Container className="analytics-container">
        <Alert variant="danger">
          <Alert.Heading>{t('dashboard.agentanalytics.configurationError')}</Alert.Heading>
          <p>{configError}</p>
          <OutlinedDangerButton onClick={() => window.location.reload()}>
            <FiRefreshCw className="me-2" /> {t('dashboard.agentanalytics.refreshPageButton')}
          </OutlinedDangerButton>
        </Alert>
      </Container>
    );
  }

  // Render error state
  if (error) {
    return (
      <Container className="analytics-container">
        <Alert variant="danger">
          <Alert.Heading>{t('dashboard.agentanalytics.errorLoadingAnalytics')}</Alert.Heading>
          <p>{error}</p>
          <OutlinedDangerButton onClick={fetchBatchAnalytics}>
            <FiRefreshCw className="me-2" /> {t('dashboard.agentanalytics.retryButton')}
          </OutlinedDangerButton>
        </Alert>
      </Container>
    );
  }

  // Render 'No Data' state if fetch was successful but returned no data
  if (!batchAnalyticsData) {
    return (
      <MantineProvider>
        <DatesProvider settings={{ locale: 'en', firstDayOfWeek: 0, weekendDays: [0] }}>
          <div className="agent-analytics-container">
            {headerComponent}

            {/* No Data Content */}
            <div className="analytics-content">
              <div className="empty-analytics-state">
                <div className="empty-state-icon">
                  <FiBarChart size={48} />
                </div>
                <h4>{t('dashboard.agentanalytics.noDataAvailableTitle')}</h4>
                <p>{t('dashboard.agentanalytics.noDataAvailableDesc')}</p>
                <OutlinedPrimaryButton onClick={fetchBatchAnalytics}>
                  <FiRefreshCw className="me-2" /> {t('dashboard.agentanalytics.refreshButton')}
                </OutlinedPrimaryButton>
              </div>
            </div>
          </div>
        </DatesProvider>
      </MantineProvider>
    );
  }

  // Prepare data for Chart.js charts
  const dailyUserChartData = transformDailyDataForChart(
    batchAnalyticsData.daily_user_counts,
    t('dashboard.agentanalytics.usersTitle'),
    USER_COLOR
  );
  const dailyQuestionChartData = transformDailyDataForChart(
    batchAnalyticsData.daily_question_counts,
    t('dashboard.agentanalytics.questionsTitle'),
    QUESTION_COLOR
  );
  const dailyLikeDislikeChartData = transformLikeDislikeDataForChart(
    batchAnalyticsData.daily_like_dislike_counts
  );
  const sourceChartData = transformCategoricalDataForChart(
    batchAnalyticsData.source_question_counts,
    t('dashboard.agentanalytics.questionsTitle'),
    SOURCE_COLOR
  );
  const modelChartData = transformCategoricalDataForChart(
    batchAnalyticsData.model_question_counts,
    t('dashboard.agentanalytics.answersLabel'),
    MODEL_COLOR
  );

  // Function to get color based on count (example simple scale)
  const getColorScale = (count) => {
    if (!count || count === 0) return "#e5e7eb"; // Light gray for no data
    // Simple scale: more questions -> darker blue
    const opacity = Math.min(0.9, 0.1 + (count / (maxCountryCount || 1)) * 0.8);
    return `rgba(30, 149, 224, ${opacity})`; // Use primary blue with varying opacity
  };

  return (
    <MantineProvider>
      <DatesProvider settings={{ locale: 'en', firstDayOfWeek: 0, weekendDays: [0] }}>
        <div className="agent-analytics-container">
          {headerComponent}

          {/* Analytics Content */}
          <div className="analytics-content">
            {/* General Overview Section */}
            <div className="analytics-section">
              <div className="section-header">
                <h4 className="section-title">
                  <FiActivity className="me-2" /> {t('dashboard.agentanalytics.generalOverviewTitle')}
                </h4>
              </div>

              <div className="charts-grid">
                {/* User Count Chart */}
                <div className="chart-container">
                  <div className="chart-header">
                    <div className="chart-title-group">
                      <FiUsers className="chart-icon" />
                      <h5 className="chart-title">{t('dashboard.agentanalytics.usersTitle')}</h5>
                    </div>
                    <span className="total-count">{formatNumber(batchAnalyticsData.total_user_count)} {t('dashboard.agentanalytics.totalLabel')}</span>
                  </div>
                  <div className="chart-wrapper">
                    <Line
                      data={dailyUserChartData}
                      options={{
                        ...commonChartOptions,
                        plugins: {
                          ...commonChartOptions.plugins,
                          tooltip: {
                            ...commonChartOptions.plugins.tooltip,
                            callbacks: {
                              label: function (context) {
                                return `${context.dataset.label}: ${formatNumber(context.parsed.y)}`;
                              }
                            }
                          }
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Question Count Chart */}
                <div className="chart-container">
                  <div className="chart-header">
                    <div className="chart-title-group">
                      <FiHelpCircle className="chart-icon" />
                      <h5 className="chart-title">{t('dashboard.agentanalytics.questionsTitle')}</h5>
                    </div>
                    <span className="total-count">{formatNumber(batchAnalyticsData.total_question_count)} {t('dashboard.agentanalytics.totalLabel')}</span>
                  </div>
                  <div className="chart-wrapper">
                    <Line
                      data={dailyQuestionChartData}
                      options={{
                        ...commonChartOptions,
                        plugins: {
                          ...commonChartOptions.plugins,
                          tooltip: {
                            ...commonChartOptions.plugins.tooltip,
                            callbacks: {
                              label: function (context) {
                                return `${context.dataset.label}: ${formatNumber(context.parsed.y)}`;
                              }
                            }
                          }
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Feedback Chart */}
                <div className="chart-container">
                  <div className="chart-header">
                    <div className="chart-title-group">
                      <FiThumbsUp className="chart-icon" />
                      <h5 className="chart-title">{t('dashboard.agentanalytics.feedbackTitle')}</h5>
                    </div>
                    <span className="total-count feedback-count">
                      <span className="liked-count">
                        <FiThumbsUp style={{ color: LIKE_COLOR }} /> {formatNumber(batchAnalyticsData.total_liked_count)}
                      </span>
                      <span className="disliked-count">
                        <FiThumbsDown style={{ color: DISLIKE_COLOR }} /> {formatNumber(batchAnalyticsData.total_disliked_count)}
                      </span>
                    </span>
                  </div>
                  <div className="chart-wrapper">
                    <Line
                      data={dailyLikeDislikeChartData}
                      options={{
                        ...commonChartOptions,
                        plugins: {
                          ...commonChartOptions.plugins,
                          tooltip: {
                            ...commonChartOptions.plugins.tooltip,
                            callbacks: {
                              label: function (context) {
                                return `${context.dataset.label}: ${formatNumber(context.parsed.y)}`;
                              }
                            }
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Geographic Distribution Section */}
            <div className="analytics-section">
              <div className="section-header">
                <h4 className="section-title">
                  <FiGlobe className="me-2" /> {t('dashboard.agentanalytics.countryDistributionTitle')}
                </h4>
              </div>

              <div className="geographic-grid">
                {/* World Map */}
                <div className="map-container">
                  <div className="map-wrapper" data-tooltip-id="country-tooltip">
                    <ComposableMap
                      projection="geoMercator"
                      projectionConfig={{
                        scale: mapScale,
                        center: mapCenter
                      }}
                    >
                      <Geographies geography={geoUrl}>
                        {({ geographies }) =>
                          geographies.map((geo) => {
                            if (geo.properties.name === "Antarctica") {
                              return null;
                            }

                            const countryName = geo.properties.name || "";
                            const countryNameLower = countryName.toLowerCase();
                            const count = countryCountsMap.get(countryNameLower) || 0;
                            const countryColor = getColorScale(count);

                            return (
                              <Geography
                                key={geo.rsmKey}
                                geography={geo}
                                onMouseEnter={() => {
                                  const countText = count > 0 ? `: ${formatNumber(count)} questions` : ": 0";
                                  setTooltipContent(`${countryName}${countText}`);
                                }}
                                onMouseLeave={() => {
                                  setTooltipContent("");
                                }}
                                style={{
                                  default: {
                                    fill: countryColor,
                                    outline: "none",
                                    stroke: "#FFF",
                                    strokeWidth: 0.5,
                                  },
                                  hover: {
                                    fill: "#ff8e03",
                                    outline: "none",
                                    stroke: "#FFF",
                                    strokeWidth: 0.75,
                                  },
                                  pressed: {
                                    fill: "#ff8e03",
                                    outline: "none",
                                  },
                                }}
                              />
                            );
                          })
                        }
                      </Geographies>
                    </ComposableMap>
                  </div>
                </div>

                {/* Country List */}
                <div className="country-list-container">
                  <div className="country-list-header">
                    <h5 className="country-list-title">{t('dashboard.agentanalytics.questionsByCountry')}</h5>
                  </div>
                  {sortedCountryData.length > 0 ? (
                    <div className="country-list-scrollable">
                      <ul className="country-list">
                        {sortedCountryData.sort((a, b) => b.count - a.count).map((item, index) => (
                          <li key={index} className="country-list-item">
                            <span className="country-name">{item.name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</span>
                            <span className="country-count">{formatNumber(item.count)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div className="no-data-message">
                      <p>{t('dashboard.agentanalytics.noCountryData')}</p>
                    </div>
                  )}
                </div>
              </div>
              <Tooltip id="country-tooltip" content={tooltipContent} />
            </div>

            {/* Detailed Breakdown Section */}
            <div className="analytics-section">
              <div className="section-header">
                <h4 className="section-title">
                  <FiBarChart className="me-2" /> {t('dashboard.agentanalytics.detailedBreakdownTitle')}
                </h4>
              </div>

              <div className="breakdown-grid">
                {/* Questions by Source */}
                <div className="chart-container">
                  <div className="chart-header">
                    <div className="chart-title-group">
                      <h5 className="chart-title">{t('dashboard.agentanalytics.questionsBySource')}</h5>
                    </div>
                  </div>
                  <div className="chart-wrapper">
                    {sourceChartData.labels.length > 0 ? (
                      <Bar
                        data={sourceChartData}
                        options={{
                          ...commonChartOptions,
                          scales: {
                            ...commonChartOptions.scales,
                            x: {
                              ...commonChartOptions.scales.x,
                              ticks: {
                                ...commonChartOptions.scales.x.ticks,
                                maxRotation: 45,
                                minRotation: 45
                              }
                            }
                          },
                          plugins: {
                            ...commonChartOptions.plugins,
                            tooltip: {
                              ...commonChartOptions.plugins.tooltip,
                              callbacks: {
                                label: function (context) {
                                  return `${context.dataset.label}: ${formatNumber(context.parsed.y)}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    ) : (
                      <div className="no-data-message">
                        <p>{t('dashboard.agentanalytics.noSourceData')}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Answers by Model */}
                <div className="chart-container">
                  <div className="chart-header">
                    <div className="chart-title-group">
                      <h5 className="chart-title">{t('dashboard.agentanalytics.answersByModel')}</h5>
                    </div>
                  </div>
                  <div className="chart-wrapper">
                    {modelChartData.labels.length > 0 ? (
                      <Bar
                        data={modelChartData}
                        options={{
                          ...commonChartOptions,
                          scales: {
                            ...commonChartOptions.scales,
                            x: {
                              ...commonChartOptions.scales.x,
                              ticks: {
                                ...commonChartOptions.scales.x.ticks,
                                maxRotation: 45,
                                minRotation: 45
                              }
                            }
                          },
                          plugins: {
                            ...commonChartOptions.plugins,
                            tooltip: {
                              ...commonChartOptions.plugins.tooltip,
                              callbacks: {
                                label: function (context) {
                                  return `${context.dataset.label}: ${formatNumber(context.parsed.y)}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    ) : (
                      <div className="no-data-message">
                        <p>{t('dashboard.agentanalytics.noModelData')}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DatesProvider>
    </MantineProvider>
  );
};

export default AgentAnalytics;
